import { Component, OnDestroy, OnInit } from '@angular/core';
import { LangChangeEvent } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApplicationPaths } from 'src/app/api-authorization/api-authorization.constants';
import { AuthorizeService } from 'src/app/api-authorization/authorize.service';
import { AccordionType } from 'src/app/shared/components/accordions-group/models/accordion-type.model';
import { InfoBoxTab } from 'src/app/shared/components/info-box/info-box-tab.model';
import { TranslationService } from 'src/metronic/core/_base/layout';
import { LanguageType } from 'src/metronic/core/_config/i18n/language-type.model';
import { HomeInfoBoxGeneratorService } from './home-info-box-generator.service';
import { HomePageService } from './home-page.service';
import { AlertData } from './models/alert.model';
import { EnrollmentData } from './models/enrollment-data.model';
import { Enrollment } from './models/enrollment.model';

@Component({
  templateUrl: './home-page.component.html',
  styleUrls: ['./home-page.component.scss']
})
export class HomePageComponent implements OnInit, OnDestroy {
  isLoaded = false;
  alerts$: Observable<AlertData[]>;
  enrollmentData: EnrollmentData;
  enrollmentInfo: string;
  isAuthenticated: boolean;

  readonly infoBoxTabs$ = new Subject<InfoBoxTab[]>();
  readonly AuthPaths = ApplicationPaths;
  readonly AccordionType = AccordionType;

  private readonly unsubscribe$ = new Subject<void>();

  constructor(
    private service: HomePageService,
    private translationService: TranslationService,
    private homeInfoBoxGeneratorService: HomeInfoBoxGeneratorService,
    private authorizeService: AuthorizeService
  ) { }

  ngOnInit() {
    this.initDataConnectedWithEnrollments(this.translationService.getCurrentLanguage());
    this.initLanguageChanges();
    this.initIsAuthenticated();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }


  private initDataConnectedWithEnrollments(language: LanguageType): void {
    this.service.initEnrollmentData();

    this.service
      .getEnrollmentData()
      .subscribe(data => {
        this.enrollmentData = data;
        this.initEnrollmentInfo(data);
        this.initEnrollmentsList(data.isActive, language);
      });

    this.alerts$ = this.service.getActiveAlerts(language);
  }

  private initEnrollmentInfo(enrollmentData: EnrollmentData): void {
    const isEnrollmentActive: boolean = enrollmentData.isActive;

    if (!isEnrollmentActive && enrollmentData.startOrEndDate == null) {
      this.translationService
        .getTranslationFor('W tej chwili nie są prowadzone żadne rekrutacje, zapraszamy w późniejszym terminie')
        .subscribe(t => this.enrollmentInfo = t);
    }
    else {
      this.translationService
        .getTranslationFor(this.getEnrollmentInfoTranslateKey(isEnrollmentActive), enrollmentData)
        .subscribe(t => this.enrollmentInfo = t);
    }
  }

  private getEnrollmentInfoTranslateKey(isEnrollmentActive: boolean): string {
    return isEnrollmentActive ? 'HOME.ROOT.ENROLLMENT-PORTAL-HEADER.ACTIVE-ENROLLMENT-INFO' : 'HOME.ROOT.ENROLLMENT-PORTAL-HEADER.INACTIVE-ENROLLMENT-INFO';
  }

  private initEnrollmentsList(isEnrollmentActive: boolean, language: LanguageType): void {
    let enrollments: Observable<Enrollment[]>;

    if (isEnrollmentActive) {
      enrollments = this.service.getActiveEnrollments(language);
    }
    else {
      enrollments = this.service.getFutureEnrollments(language);
    }

    enrollments.subscribe(enr => {
      this.initInfoBox(enr, language);
    });
  }

  private initInfoBox(enrollments: Enrollment[], language: LanguageType): void {
    const infoBoxItems = this.homeInfoBoxGeneratorService.generateInfoBoxItems(enrollments, language);
    this.infoBoxTabs$.next(infoBoxItems);
    this.isLoaded = true;
  }

  private initLanguageChanges(): void {
    this.translationService.onLanguageChange()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((lce: LangChangeEvent) =>
        this.initDataConnectedWithEnrollments(LanguageType[lce.lang])
      );
  }

  private initIsAuthenticated(): void {
    this.authorizeService.isAuthenticated()
      .subscribe(isAuthenticated => this.isAuthenticated = isAuthenticated);
  }
}
